import { ColumnDef } from "@tanstack/react-table";
import { Avatar } from "@mui/material";
import {
	enumToStringConverter,
	formatDate,
	moneyFormat,
} from "../../../helpers/appHelpers";
import { getLeaveRequestStatusColor } from "../../self_service/leave_management/data";

// Type definitions
interface Employee {
	firstName: string;
	lastName: string;
	photoUrl?: string;
}

interface LeaveType {
	name: string;
	colorCode: string;
}

interface LeavePackage {
	leaveType: LeaveType;
}

interface CompanyLevel {
	id: number;
	name?: string;
	levelName?: string;
}

interface CompanyLevelLeavePackage {
	id: number;
	companyLevelId: number;
	leavePackageId: number;
	companyLevel?: CompanyLevel;
}

interface LeaveRequestRow {
	id: number;
	employee?: Employee;
	startDate: string;
	returnDate: string;
	status: "PENDING" | "APPROVED" | "DECLINED" | "CANCELLED";
	comment: string;
	leavePackage?: LeavePackage;
	createdAt?: string;
	modifiedAt?: string;
}

interface LeavePackageRow {
	id: number;
	name: string;
	code: string;
	description: string;
	leaveType: LeaveType;
	maxDays: number;
	paid: boolean;
	redeemable: boolean;
	accrued: boolean;
	carryOverDaysValue: number;
	carryOverDaysPercent?: number;
	companyLevelLeavePackages?: CompanyLevelLeavePackage[];
	createdAt?: string;
	modifiedAt?: string;
}

// ========== Leave Requests Columns (HR) ==========
export const leaveRequestsColumnsHR: ColumnDef<LeaveRequestRow>[] = [
	{
		id: "employee",
		accessorKey: "employee",
		header: "Employee",
		size: 250,
		cell: ({ row }) => {
			const employee = row.original.employee;
			if (!employee) return null;

			return (
				<div className="flex items-center space-x-4">
					<Avatar
						src={employee.photoUrl || ""}
						alt="Dp"
						className="w-10 h-10 rounded-full"
					/>
					<p className="truncate">
						{(employee.firstName || "") + " " + (employee.lastName || "")}
					</p>
				</div>
			);
		},
	},
	{
		id: "startDate",
		accessorKey: "startDate",
		header: "Start Date",
		size: 200,
		cell: ({ row }) => formatDate(row.original.startDate),
		sortingFn: (rowA, rowB) => {
			const dateA = new Date(rowA.original.startDate).getTime();
			const dateB = new Date(rowB.original.startDate).getTime();
			return dateA - dateB;
		},
	},
	{
		id: "returnDate",
		accessorKey: "returnDate",
		header: "End Date",
		size: 200,
		cell: ({ row }) => formatDate(row.original.returnDate),
		sortingFn: (rowA, rowB) => {
			const dateA = new Date(rowA.original.returnDate).getTime();
			const dateB = new Date(rowB.original.returnDate).getTime();
			return dateA - dateB;
		},
	},
	{
		id: "status",
		accessorKey: "status",
		header: "Status",
		size: 200,
		cell: ({ row }) => {
			const status = row.original.status as
				| "PENDING"
				| "APPROVED"
				| "DECLINED"
				| "CANCELLED";
			const color = getLeaveRequestStatusColor(status);

			return (
				<div
					className="rounded-lg px-4 py-2 min-w-[80px] text-center relative font-semibold overflow-hidden"
					style={{ color }}
				>
					<div
						className="absolute inset-0"
						style={{
							background: color,
							opacity: "15%",
						}}
					/>
					<p>{enumToStringConverter(status)}</p>
				</div>
			);
		},
	},
	{
		id: "comment",
		accessorKey: "comment",
		header: "Comment",
		size: 350,
	},
	{
		id: "leavePackage",
		accessorKey: "leavePackage",
		header: "Leave Type",
		size: 220,
		cell: ({ row }) => {
			const leaveType = row.original.leavePackage?.leaveType;
			if (!leaveType) return null;

			return (
				<div
					className="rounded-lg px-4 py-2 min-w-[80px] text-center relative font-semibold overflow-hidden"
					style={{ color: leaveType.colorCode }}
				>
					<div
						className="absolute inset-0"
						style={{
							background: leaveType.colorCode,
							opacity: "15%",
						}}
					/>
					<p>{leaveType.name}</p>
				</div>
			);
		},
	},
];

export const leaveRequestColumnVisibility: Record<string, boolean> = {
	employee: true,
	startDate: true,
	returnDate: true,
	status: true,
	comment: true,
	leavePackage: true,
};

// ========== Leave Package Columns ==========
export const leavePackageColumns: ColumnDef<LeavePackageRow>[] = [
	{
		id: "name",
		accessorKey: "name",
		header: "Name",
		size: 220,
	},
	{
		id: "code",
		accessorKey: "code",
		header: "Code",
		size: 200,
	},
	{
		id: "description",
		accessorKey: "description",
		header: "Description",
		size: 380,
	},
	{
		id: "leaveType",
		accessorKey: "leaveType",
		header: "Leave Type",
		size: 200,
		cell: ({ row }) => {
			const leaveType = row.original.leaveType;
			if (!leaveType) return null;

			return (
				<div
					className="rounded-lg px-4 py-2 min-w-[80px] text-center relative font-semibold overflow-hidden"
					style={{ color: leaveType.colorCode }}
				>
					<div
						className="absolute inset-0"
						style={{
							background: leaveType.colorCode,
							opacity: "15%",
						}}
					/>
					<p>{leaveType.name}</p>
				</div>
			);
		},
	},
	{
		id: "maxDays",
		accessorKey: "maxDays",
		header: "Max Days",
		size: 180,
	},
	{
		id: "paid",
		accessorKey: "paid",
		header: "Paid",
		size: 140,
		cell: ({ row }) => (row.original.paid ? "Yes" : "No"),
	},
	{
		id: "redeemable",
		accessorKey: "redeemable",
		header: "Redeemable",
		size: 150,
		cell: ({ row }) => (row.original.redeemable ? "Yes" : "No"),
	},
	{
		id: "accrued",
		accessorKey: "accrued",
		header: "Accrued",
		size: 150,
		cell: ({ row }) => (row.original.accrued ? "Yes" : "No"),
	},
	{
		id: "companyLevels",
		accessorKey: "companyLevelLeavePackages",
		header: "Linked Company Levels",
		size: 250,
		cell: ({ row }) => {
			const companyLevelLeavePackages =
				row.original.companyLevelLeavePackages || [];

			if (companyLevelLeavePackages.length === 0) {
				return <span className="text-gray-400">None</span>;
			}

			// Extract company level names from the junction table
			const levelNames = companyLevelLeavePackages
				.map((junction) => {
					const level = junction.companyLevel;
					return level?.levelName || level?.name;
				})
				.filter(Boolean)
				.join(", ");

			return (
				<div className="truncate" title={levelNames}>
					{levelNames}
				</div>
			);
		},
	},
	{
		id: "carryOverDaysValue",
		accessorKey: "carryOverDaysValue",
		header: "Carry Over Days Amount",
		size: 260,
		cell: ({ row }) => moneyFormat(row.original.carryOverDaysValue),
	},
	{
		id: "carryOverDaysPercent",
		accessorKey: "carryOverDaysPercent",
		header: "Carry Over Days Percentage",
		size: 260,
		cell: ({ row }) => row.original.carryOverDaysPercent || "--",
	},
];

export const leavePackageColumnVisibility: Record<string, boolean> = {
	name: true,
	code: true,
	description: true,
	leaveType: true,
	maxDays: true,
	paid: true,
	redeemable: true,
	accrued: true,
	companyLevels: true,
	carryOverDaysValue: false,
	carryOverDaysPercent: false,
};
